import { memo } from "react";
import type { FC } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import MstaStyle from "../../Functions/Style/style.module.css";
import { Box, CircularProgress } from "@mui/material";
import { Grid } from "@mui/material";
import { Card, CardActionArea } from "@mui/material";
import { Typography } from "@mui/material";
import { MstaFontFamily } from "../../Functions/Style/MstaFont";
import { Paper } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EastIcon from "@mui/icons-material/East";
import SchoolIcon from "@mui/icons-material/School";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ListIcon from '@mui/icons-material/List';
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import { lightBlue } from "@mui/material/colors";
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import { MstaButton } from "../../Functions/Style/ButtonStyle";
import { getDataByGas } from "../../Functions/APIservice/gasAPI";

// カスタムアイコン
const CTMRIIcon = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 1200 1200" {...props}>
    <path d="M0 0h1200v1200H0z" fill="none" />
    <path fill="currentColor" d="M454.771 10.997c-76.209-40.113-226.406 37.395-269.553 105.63c-19.222 30.534-17.862 52.538-17.862 65.022v667.874L730.602 1200l105.917-57.833V491.739L258.215 159.706c31.033-39.057 100.827-86.683 153.16-67.555l515.104 275.498l.001 724.58l106.184-57.936V309.728z" />
  </SvgIcon>
);

const MStudyIcon = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 256 256" {...props}>
    <path d="M0 0h256v256H0z" fill="none" />
    <path fill="currentColor" d="M201.54 54.46A104 104 0 0 0 54.46 201.54A104 104 0 0 0 201.54 54.46M88 192a16 16 0 0 1 32 0v23.59a88 88 0 0 1-32-9.22Zm48 0a16 16 0 0 1 32 0v14.37a88 88 0 0 1-32 9.22Zm-28.73-56h41.46l11.58 25.1a31.93 31.93 0 0 0-32.31 9.77a31.93 31.93 0 0 0-32.31-9.77Zm7.39-16L128 91.09L141.34 120Zm75.56 70.23c-2 2-4.08 3.87-6.22 5.64V176a7.9 7.9 0 0 0-.74-3.35l-48-104a8 8 0 0 0-14.52 0l-48 104A7.9 7.9 0 0 0 72 176v19.87a89 89 0 0 1-6.22-5.64a88 88 0 1 1 124.44 0" />
  </SvgIcon>
);

interface Props {
  className?: string;
}
/* @figmaId 5:77 */
const Home: FC<Props> = memo(function Home(props = {}) {
  const navigate = useNavigate();
  const [searchWord, setSearchWord] = React.useState("");
  const [composing, setComposition] = React.useState(false);
  const [news, setNews] = React.useState<
    {
      date: string;
      category: string;
      summary: string;
      content: string;
    }[]
  >([]);

  const startComposition = () => setComposition(true);
  const endComposition = () => setComposition(false);

  const handleOpenLink = (url: string) => {
    const toUrl = url;
    navigate(toUrl);
  };

  const handleOpenBlankLink = (url: string) => {
    const toUrl = url;
    window.open(toUrl, "_blank");
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (composing === false) {
        // Perform search or any other desired action
        navigate(`/catalogue/search?keyword=${searchWord}`);
      }
    }
  };

  React.useEffect(() => {
    getDataByGas({
      mode: "UpdateInformation",
    }).then((response) => {
      response
        .json()
        .then(
          (
            docx: {
              date: string;
              category: string;
              summary: string;
              content: string;
            }[]
          ) => {
            setNews(docx);
          }
        )
        .catch((error) => {
          console.error("Error:", error);
        });
    });
  }, [news]);

  return (
    <Grid minHeight={"95vh"}>
      <Grid display="grid" alignItems="center" justifyContent="center">
        <Typography
          variant="h4"
          align="center"
          fontFamily={MstaFontFamily}
          className={MstaStyle.title}
          sx={{ m: 3 }}
        >
          どんな症例をお探しですか?
        </Typography>
        <Paper
          sx={{
            p: "2px 4px",
            m: 2,
            display: "flex",
            alignItems: "center",
            minHeight: { xs: 35, md: 70 },
          }}
          onKeyDown={handleKeyPress}
        >
          <SearchIcon sx={{ fontSize: { md: 60, xs: 30 }, color: "gray" }} />
          <InputBase
            sx={{
              ml: 1,
              flex: 1,
              fontSize: { md: 25, xs: 16 },
              fontFamily: MstaFontFamily,
            }}
            placeholder="症例を検索する"
            inputProps={{ "aria-label": "search" }}
            onCompositionStart={startComposition}
            onCompositionEnd={endComposition}
            value={searchWord}
            onChange={(event) => {
              setSearchWord(event.target.value);
            }}
          />
          <Divider orientation="vertical" variant="middle" flexItem />
          <IconButton
            type="button"
            sx={{ p: "10px" }}
            aria-label="search"
            onClick={() => {
              handleOpenLink(`/catalogue/search?keyword=${searchWord}`);
            }}
          >
            <EastIcon sx={{ fontSize: { md: 60, xs: 30 }, color: "gray" }} />
          </IconButton>
        </Paper>
        <Typography
          variant="subtitle1"
          align="center"
          fontFamily={MstaFontFamily}
          fontSize={20}
        >
          日本初！
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          fontFamily={MstaFontFamily}
          fontSize={20}
        >
          CTやMRIなど、医療画像の症例検索サイト
        </Typography>

        <Grid
          display="grid"
          gridTemplateColumns={{ md: "repeat(3, 1fr)", xs: "repeat(2, 1fr)" }}
          gap={2}
          mt={2}
          width="100%"
          maxWidth={{ xs: "100%", md: 1000 }}
          px={{ xs: 2, md: 4 }}
          mx="auto"
        >
          <Card sx={{ border: 1, borderColor: lightBlue[400] }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenLink("/basic");
              }}
            >
              <SchoolIcon
                sx={{ fontSize: 90, color: lightBlue[400] }}
              ></SchoolIcon>
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={lightBlue[400]}
              >
                正常画像
              </Typography>
            </CardActionArea>
          </Card>
          <Card sx={{ border: 1, borderColor: lightBlue[400] }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenLink("/catalogue/search");
              }}
            >
              <SearchIcon
                sx={{ fontSize: 90, color: lightBlue[400] }}
              ></SearchIcon>
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={lightBlue[400]}
              >
                症例検索
              </Typography>
            </CardActionArea>
          </Card>
          <Card sx={{ border: 1, borderColor: lightBlue[400] }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenLink("/catalogue/disease");
              }}
            >
              <ListIcon sx={{ fontSize: 90, color: lightBlue[400] }} />
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={lightBlue[400]}
              >
                疾患一覧
              </Typography>
            </CardActionArea>
          </Card>

          <Card sx={{ border: 1, borderColor: lightBlue[400] }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenLink("/mstudy");
              }}
            >
              <Box display="flex" justifyContent="center">
                <MStudyIcon sx={{ fontSize: 90, color: lightBlue[400] }} />
              </Box>
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={lightBlue[400]}
              >
                問題演習
              </Typography>
            </CardActionArea>
          </Card>

          <Card sx={{ border: 1, borderColor: lightBlue[400] }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenBlankLink("https://sites.google.com/mnes.org/msta-tutorial/%E3%83%9B%E3%83%BC%E3%83%A0");
              }}
            >
              <Box display="flex" justifyContent="center">
                <CTMRIIcon sx={{ fontSize: 90, color: lightBlue[400] }} />
              </Box>
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={lightBlue[400]}
              >
                CT・MRI入門
              </Typography>
            </CardActionArea>
          </Card>

          <Card sx={{ border: 1, borderColor: "gray" }}>
            <CardActionArea
              sx={{ height: 150, pt: 2 }}
              onClick={() => {
                handleOpenLink("/mypage/account");
              }}
            >
              <AccountCircleIcon
                sx={{ fontSize: 90, color: "gray" }}
              ></AccountCircleIcon>
              <Typography
                variant="subtitle1"
                align="center"
                fontFamily={MstaFontFamily}
                fontSize={16}
                color={"gray"}
              >
                マイページ
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>

        <Typography
          variant="h4"
          align="left"
          fontFamily={MstaFontFamily}
          sx={{ mx: 1, my: 3 }}
        >
          NEWS
        </Typography>

        {news.length > 0 ? (
          <Grid container justifyContent="center" mb={4}>
            <Grid item maxWidth={{ xs: "90vw", md: 700 }}>
              {news.map((item, index) => (
                <Accordion
                  key={index.toString()}
                  disableGutters
                  square
                  sx={{
                    boxShadow: "none",
                    borderTop: "1px solid gray",
                    "&:last-child": {
                      borderBottom: "1px solid gray",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ pl: 0.5 }}
                  >
                    <Grid>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        gap={2}
                      >
                        <Typography
                          variant="subtitle1"
                          align="justify"
                          fontSize={16}
                          fontFamily={MstaFontFamily}
                          sx={{ width: 90 }}
                        >
                          {item.date}
                        </Typography>
                        <Typography
                          sx={{
                            bgcolor:
                              item.category === "プレリリース"
                                ? "#6FC0F3"
                                : item.category === "イベント情報"
                                ? "#FDC1B9"
                                : item.category === "アップデート"
                                ? "#A8DF8E"
                                : "#9CA4AD",
                            color: "white",
                            // textShadow: "1px 1px 1px rgba(0,0,0,0.2)",
                            borderRadius: 4,
                            fontSize: 14,
                            fontWeight: "bold",
                            py: 0.3,
                            px: 1.3,
                          }}
                        >
                          {item.category}
                        </Typography>
                      </Grid>
                      <Typography
                        variant="subtitle1"
                        align="justify"
                        fontSize={22}
                        fontFamily={MstaFontFamily}
                        // fontWeight="bold"
                        my={0.5}
                      >
                        {item.summary}
                      </Typography>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography
                      variant="subtitle1"
                      align="justify"
                      fontSize={16}
                      fontFamily={MstaFontFamily}
                      mb={2}
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {item.content}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="center" mb={4}>
            <Grid item width={{ xs: "90vw", md: 700 }}>
              <CircularProgress />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
});

export default function LoadedHome(): JSX.Element {
  return (
    <div>
      <Home />
    </div>
  );
}
